import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BoxesIcon, UploadIcon, EditIcon, CheckCircle2Icon, AlertTriangleIcon,
  FileSpreadsheetIcon, HistoryIcon, WalletIcon,
} from "lucide-react";
import * as XLSX from "xlsx";

/* ============ OPERAÇÃO LOGÍSTICA — 7 ABAS (ciclo de vida do contentor) ============ */

const ABAS = [
  { key: "recebimento", etapa: "RECEBIMENTO", num: 1, label: "Recebimento", cor: "#00E5FF",
    desc: "Certificar o físico e lançar o Nº MIGO do SAP — obrigatório para efetivar a entrada no estoque.",
    campos: [{ f: "migo", l: "Nº MIGO (SAP)" }],
    excelHint: "Colunas: CONTENTOR, MIGO" },
  { key: "planejamento", etapa: "PLANEJAMENTO", num: 2, label: "Planejamento", cor: "#FFD60A",
    desc: "Dados de transporte e COMEX. Ao vincular a RESERVA, o sistema DEBITA o saldo do produto automaticamente.",
    campos: [{ f: "reserva", l: "Reserva" }, { f: "uf_frete", l: "UF (Unid. Frete)" }, { f: "uc", l: "UC (Unid. Carga)" }, { f: "fwo", l: "FWO (Forwarding Order)" }],
    excelHint: "Colunas: CONTENTOR, RESERVA, RT (→UF), NM (→UC)" },
  { key: "nf-armazenagem", etapa: "NF_ARMAZENAGEM", num: 3, label: "NF de Armazenagem", cor: "#BF5AF2",
    desc: "Lastro fiscal da entrada e permanência dos ativos no armazém.",
    campos: [{ f: "dsm", l: "Nº DSM" }, { f: "nf_armazenagem_produto", l: "NF Armaz. Produto" }, { f: "nf_armazenagem_contentor", l: "NF Armaz. Contentor" }],
    excelHint: "Colunas: CONTENTOR, DSM, NF do Produto, NF do Contentor" },
  { key: "atendimento", etapa: "ATENDIMENTO", num: 4, label: "Central de Atendimento", cor: "#FF9F0A",
    desc: "Amarração de Ordem e Remessa por contentor. Chave de busca: CONTENTOR + RESERVA.",
    campos: [{ f: "zdev_ordem", l: "Ordem (ZDEV)" }, { f: "remessa", l: "Remessa" }],
    excelHint: "Colunas: CONTENTOR, RESERVA, ZDEV (→Ordem), REMESSA" },
  { key: "expedicao", etapa: "EXPEDICAO", num: 5, label: "Expedição", cor: "#34C759",
    desc: "Montagem da carga para o Porto: Cesta, Carreta, Cluster e Data de Corte → fila de Faturamento.",
    campos: [{ f: "cesta", l: "Cesta Acoplada" }, { f: "carreta_placa", l: "Placa da Carreta" }, { f: "cluster", l: "Cluster" }, { f: "data_corte", l: "Data de Corte" }],
    excelHint: "Colunas: CONTENTOR, Cesta, Carreta, Cluster, Data de Corte" },
  { key: "faturamento", etapa: "FATURAMENTO", num: 6, label: "Faturamento", cor: "#FF3B30",
    desc: "Emissão das NFs de Devolução/Remessa que acompanham o veículo até o porto (apartada ou conjunta).",
    campos: [{ f: "nf_dev_produto", l: "NF Dev. Produto" }, { f: "nf_dev_contentor", l: "NF Contentor" }, { f: "nf_dev_cesta", l: "NF Dev. Cesta" }],
    excelHint: "Colunas: CONTENTOR, NF de Devolução do Produto, NF do Contentor, NF de Devolução da Cesta" },
  { key: "devolucao", etapa: "DEVOLUCAO", num: 7, label: "Devolução de Vazios", cor: "#0A84FF",
    desc: "Fechamento do ciclo reverso: NF de retorno + veículo da transportadora. Bipagem na balança finaliza o ciclo.",
    campos: [{ f: "nf_retorno_vazio", l: "NF Retorno de Vazio" }, { f: "veiculo_transportadora", l: "Veículo Transportadora" }],
    excelHint: "Colunas: CONTENTOR, NF de Retorno de Vazio, Veículo" },
];

export default function OperacaoLogistica() {
  const [abaKey, setAbaKey] = useState("recebimento");
  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState("");
  const [saldos, setSaldos] = useState([]);
  const [showSaldos, setShowSaldos] = useState(false);
  const [contagens, setContagens] = useState({});
  const fileRef = useRef(null);
  const [importResult, setImportResult] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editCampos, setEditCampos] = useState({});
  const [histItem, setHistItem] = useState(null);

  const aba = ABAS.find((a) => a.key === abaKey);

  const load = async () => {
    const [r, s] = await Promise.all([
      api.get(`/logistica/itens?etapa=${aba.etapa}`).catch(() => ({ data: [] })),
      api.get("/logistica/saldos").catch(() => ({ data: [] })),
    ]);
    setItens(r.data);
    setSaldos(s.data);
    // Contagem de todas as etapas para os badges
    const all = await api.get("/logistica/itens").catch(() => ({ data: [] }));
    const c = {};
    all.data.forEach((i) => { c[i.etapa] = (c[i.etapa] || 0) + 1; });
    setContagens(c);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [abaKey]);

  const importar = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) return toast.error("Use .xlsx, .xls ou .csv");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "", raw: false });
      if (!rows.length) return toast.error("Planilha vazia");
      const { data } = await api.post(`/logistica/import/${aba.key}`, { rows, filename: file.name });
      setImportResult({ aba: aba.label, ...data });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const abrirEdicao = (it) => {
    const c = {};
    aba.campos.forEach(({ f }) => { c[f] = it[f] || ""; });
    setEditCampos(c);
    setEditItem(it);
  };

  const salvarEdicao = async () => {
    try {
      const { data } = await api.patch(`/logistica/itens/${editItem.id}`, { aba: aba.key, campos: editCampos });
      toast.success(data.avancou_etapa ? "Dados salvos — contentor avançou para a próxima etapa!" : "Dados salvos");
      setEditItem(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtrados = useMemo(() => itens.filter((i) => {
    if (!busca) return true;
    const s = busca.toLowerCase();
    return ["contentor", "produto", "pedido", "reserva", "remessa", "migo", "fornecedor_nome"].some((k) => String(i[k] || "").toLowerCase().includes(s));
  }), [itens, busca]);

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="operacao-logistica-page">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1 flex items-center gap-2">
            <BoxesIcon className="w-4 h-4" /> Armazenagem & Expedição · Ciclo do Contentor
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-none">Operação <span className="text-[#FFD60A]">Logística</span></h1>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          <Badge className="rounded-none bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/40 text-[10px]" data-testid="badge-finalizados">
            {contagens["FINALIZADO"] || 0} ciclos finalizados
          </Badge>
          <Button variant="outline" onClick={() => setShowSaldos(true)} data-testid="btn-saldos" className="rounded-none border-[#FFD60A] text-[#FFD60A] hover:bg-[#FFD60A]/10 text-xs font-bold uppercase h-9">
            <WalletIcon className="w-3.5 h-3.5 mr-1" /> Saldos de Estoque
          </Button>
        </div>
      </div>

      {/* Stepper de abas */}
      <div className="flex flex-wrap border border-border" data-testid="abas-stepper">
        {ABAS.map((a) => (
          <button key={a.key} onClick={() => setAbaKey(a.key)} data-testid={`aba-${a.key}`}
                  className={`px-4 h-12 text-[10px] font-bold uppercase tracking-wider flex items-center gap-2 border-r border-border last:border-r-0 ${abaKey === a.key ? "text-black" : "bg-[#14151A] text-muted-foreground hover:text-white"}`}
                  style={abaKey === a.key ? { backgroundColor: a.cor } : {}}>
            <span className={`w-5 h-5 flex items-center justify-center text-[10px] font-black border ${abaKey === a.key ? "border-black/40" : "border-border"}`}>{a.num}</span>
            {a.label}
            <span className={`font-mono ${abaKey === a.key ? "text-black/70" : "text-[#00E5FF]"}`}>{contagens[a.etapa] || 0}</span>
          </button>
        ))}
      </div>

      {/* Descrição + ações da aba */}
      <Card className="bg-[#14151A] border-border rounded-none p-4 flex flex-wrap items-center gap-3" style={{ borderLeft: `4px solid ${aba.cor}` }}>
        <div className="flex-1 min-w-[280px]">
          <div className="text-sm font-bold" style={{ color: aba.cor }}>{aba.num}. {aba.label}</div>
          <div className="text-xs text-muted-foreground">{aba.desc}</div>
        </div>
        <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar contentor, produto..." data-testid="busca-itens" className="rounded-none bg-[#0A0A0C] border-border h-10 w-56" />
        <label className="rounded-none h-10 px-4 font-bold uppercase tracking-wider text-xs flex items-center gap-2 cursor-pointer text-black" style={{ backgroundColor: aba.cor }} data-testid="btn-importar">
          <UploadIcon className="w-4 h-4" /> Importar Excel
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => importar(e.target.files[0])} className="hidden" data-testid="input-import" />
        </label>
        <div className="w-full text-[10px] text-muted-foreground font-mono">{aba.excelHint} · Chave mestre: CONTENTOR{aba.key === "atendimento" ? " + RESERVA" : ""} · Só os campos desta aba são atualizados</div>
      </Card>

      {/* Tabela da aba */}
      <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto">
        <table className="w-full text-xs" data-testid="tabela-itens">
          <thead>
            <tr className="text-left text-[9px] uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="px-3 py-2">Contentor</th>
              <th className="px-3 py-2">Produto</th>
              <th className="px-3 py-2">Qtd Receb.</th>
              <th className="px-3 py-2">Fornecedor</th>
              <th className="px-3 py-2">Agend.</th>
              {aba.key !== "recebimento" && <th className="px-3 py-2">Reserva</th>}
              {aba.campos.map(({ f, l }) => <th key={f} className="px-3 py-2" style={{ color: aba.cor }}>{l}</th>)}
              <th className="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 && (
              <tr><td colSpan={7 + aba.campos.length} className="px-4 py-10 text-center text-muted-foreground">
                Nenhum contentor aguardando nesta etapa.
              </td></tr>
            )}
            {filtrados.map((it) => (
              <tr key={it.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`item-row-${it.contentor}`}>
                <td className="px-3 py-2 font-mono font-bold text-[#00E5FF]">{it.contentor}</td>
                <td className="px-3 py-2">{it.produto || "—"}</td>
                <td className="px-3 py-2 font-mono">{it.qtd_recebida ?? it.qtd ?? "—"}</td>
                <td className="px-3 py-2">{it.fornecedor_nome || "—"}</td>
                <td className="px-3 py-2 font-mono text-[#FFD60A]">{it.agendamento_numero || "—"}</td>
                {aba.key !== "recebimento" && <td className="px-3 py-2 font-mono">{it.reserva || "—"}</td>}
                {aba.campos.map(({ f }) => (
                  <td key={f} className="px-3 py-2 font-mono">{it[f] || <span className="text-muted-foreground/50">pendente</span>}</td>
                ))}
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => abrirEdicao(it)} data-testid={`edit-${it.contentor}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#FFD60A]">
                      <EditIcon className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setHistItem(it)} data-testid={`hist-${it.contentor}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#00E5FF]">
                      <HistoryIcon className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* MODAL RESULTADO DA IMPORTAÇÃO */}
      <Dialog open={!!importResult} onOpenChange={(v) => !v && setImportResult(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-lg" data-testid="modal-import-result">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <FileSpreadsheetIcon className="w-5 h-5 text-[#34C759]" /> Resultado da Importação · {importResult?.aba}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3 border border-[#34C759]/40 bg-[#34C759]/10 p-4">
              <CheckCircle2Icon className="w-8 h-8 text-[#34C759]" />
              <div className="text-lg font-bold text-[#34C759]" data-testid="import-sucesso">
                {importResult?.vinculados} registro(s) vinculado(s) com sucesso!
              </div>
            </div>
            {!!importResult?.nao_encontrados?.length && (
              <div className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-3" data-testid="import-alertas">
                <div className="flex items-center gap-2 text-[#FF9F0A] font-bold text-sm mb-2">
                  <AlertTriangleIcon className="w-4 h-4" /> {importResult.nao_encontrados.length} não encontrado(s) no banco — confira:
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {importResult.nao_encontrados.map((n, i) => (
                    <div key={i} className="font-mono text-[11px] text-white/70">• {n}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setImportResult(null)} className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase">Entendido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL EDIÇÃO MANUAL */}
      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-md" data-testid="modal-edit-item">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider">
              {aba.label} · <span className="font-mono text-[#00E5FF]">{editItem?.contentor}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {aba.campos.map(({ f, l }) => (
              <div key={f}>
                <Label className="text-[10px] uppercase tracking-wider" style={{ color: aba.cor }}>{l}</Label>
                <Input value={editCampos[f] || ""} onChange={(e) => setEditCampos({ ...editCampos, [f]: e.target.value })}
                       data-testid={`edit-campo-${f}`} type={f === "data_corte" ? "date" : "text"}
                       className="mt-1 rounded-none bg-[#0A0A0C] border-border h-10 font-mono" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={salvarEdicao} data-testid="salvar-edicao" className="rounded-none text-black font-bold uppercase tracking-wider" style={{ backgroundColor: aba.cor }}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL HISTÓRICO */}
      <Dialog open={!!histItem} onOpenChange={(v) => !v && setHistItem(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-md" data-testid="modal-historico">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <HistoryIcon className="w-5 h-5 text-[#00E5FF]" /> Histórico · <span className="font-mono">{histItem?.contentor}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(histItem?.historico || []).map((h, i) => (
              <div key={i} className="flex items-center gap-3 border-l-2 border-[#00E5FF]/40 pl-3 py-1">
                <div className="font-mono text-[10px] text-muted-foreground w-32">{new Date(h.ts).toLocaleString("pt-BR")}</div>
                <Badge className="rounded-none bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30 text-[9px]">{h.etapa}</Badge>
                <div className="text-[11px] text-muted-foreground">{h.user}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL SALDOS */}
      <Dialog open={showSaldos} onOpenChange={setShowSaldos}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-md" data-testid="modal-saldos">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <WalletIcon className="w-5 h-5 text-[#FFD60A]" /> Saldos de Estoque (Operação Logística)
            </DialogTitle>
          </DialogHeader>
          <div className="text-[10px] text-muted-foreground">Crédito na entrada (MIGO) · Débito quando a Reserva é atendida no Planejamento</div>
          <div className="divide-y divide-border/50 max-h-72 overflow-y-auto">
            {saldos.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">Sem movimentos de saldo ainda.</div>}
            {saldos.map((s) => (
              <div key={s.produto} className="flex justify-between py-2" data-testid={`saldo-${s.produto}`}>
                <span className="text-sm">{s.produto}</span>
                <span className={`font-mono font-bold ${s.saldo >= 0 ? "text-[#34C759]" : "text-[#FF3B30]"}`}>{s.saldo.toLocaleString("pt-BR")}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
